import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "motion/react";
import blogs from "./blogData";
import "../Styles/Blog.css";
import "../Styles/Animations.css";
import { RevealGroup, RevealItem } from "./Reveal";
import MagneticButton from "./MagneticButton";

export default function Blog() {
  const [visibleBlogs, setVisibleBlogs] = useState(9);
  const [searchQuery, setSearchQuery] = useState("");
  
  const sortedBlogs = [...blogs].sort((a, b) => new Date(b.date) - new Date(a.date));

  const filteredBlogs = sortedBlogs.filter((blog) => {
    const query = searchQuery.toLowerCase();
    return (
      blog.title.toLowerCase().includes(query) ||
      blog.category.toLowerCase().includes(query) ||
      blog.excerpt.toLowerCase().includes(query) ||
      (blog.content && blog.content.toLowerCase().includes(query))
    );
  });

  const handleViewMore = () => {
    setVisibleBlogs((prevVisible) => prevVisible + 9);
  };

  return (
    <>
      <Helmet>
        <title>Study Abroad & Immigration Blog | Insights & Visa Tips</title>
        <meta name="description" content="Stay updated with the latest guides, checklists, and tips on Canada PR, study permits, Schengen visas, and international job opportunities. Read more!" />
        <link rel="canonical" href="https://veabroad.com/blog" />
        <meta property="og:title" content="Study Abroad & Immigration Blog | Insights & Visa Tips" />
        <meta property="og:description" content="Stay updated with the latest guides, checklists, and tips on Canada PR, study permits, Schengen visas, and international job opportunities. Read more!" />
        <meta property="og:url" content="https://veabroad.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Study Abroad & Immigration Blog | Insights & Visa Tips" />
        <meta name="twitter:description" content="Stay updated with the latest guides, checklists, and tips on Canada PR, study permits, Schengen visas, and international job opportunities. Read more!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "VE Abroad Blog",
            "description": "Expert insights on immigration, study abroad, and visa guidance.",
            "publisher": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            }
          })}
        </script>
      </Helmet>

      <section className="blog-hero position-relative" style={{ background: 'linear-gradient(135deg, #0d2a2b 0%, #36948c 100%)', padding: '100px 0 80px', overflow: 'hidden' }}>
        {/* Animated Background Orbs */}
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ width: '400px', height: '400px', top: '-100px', right: '-100px' }}></div>
          <div className="va-blob va-blob-teal" style={{ width: '300px', height: '300px', bottom: '-50px', left: '-50px' }}></div>
        </div>

        <div className="container position-relative z-1">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <span className="hero-badge mb-3 d-inline-block text-white px-3 py-1 rounded-pill" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>LATEST UPDATES</span>
            <h1 className="display-4 fw-bold text-white mb-3">Our <span className="va-shine-text">Blog</span></h1>
            <p className="lead text-white-50 mx-auto mb-4" style={{ maxWidth: '600px' }}>Expert insights on immigration, study abroad, and visa guidance</p>
            
            {/* Search Bar */}
            <div className="search-bar-container mx-auto" style={{ maxWidth: '500px' }}>
              <div className="position-relative">
                <input
                  id="blog-search-input"
                  aria-label="Search articles by title, category, or content"
                  type="text"
                  placeholder="Search articles by title, category, or content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="form-control text-white border-0 py-3 ps-5 pe-5 shadow-none"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '50px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                />
                <span 
                  className="position-absolute top-50 translate-middle-y start-0 ms-3 text-white-50 d-flex align-items-center"
                  style={{ zIndex: 2, pointerEvents: 'none' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                  </svg>
                </span>
                {searchQuery && (
                  <button
                    id="blog-search-clear-btn"
                    aria-label="Clear search input"
                    onClick={() => setSearchQuery("")}
                    className="position-absolute top-50 translate-middle-y end-0 me-3 btn p-0 text-white-50 border-0 d-flex align-items-center"
                    style={{ background: 'none', zIndex: 2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-x-lg" viewBox="0 0 16 16">
                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="blog-list container py-5">
        <RevealGroup as="div" className="row g-4" stagger={0.08} amount={0.1}>
          {filteredBlogs.length === 0 ? (
            <div className="col-12 text-center py-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-5 px-4 glass-card mx-auto shadow-sm"
                style={{ maxWidth: '500px', borderRadius: '24px', background: '#fff', border: '1px solid rgba(0,0,0,0.05)' }}
              >
                <div className="mb-4 text-muted d-inline-flex p-3 rounded-circle" style={{ background: '#f5fafa', color: '#36948c' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" className="bi bi-search text-secondary" viewBox="0 0 16 16">
                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                  </svg>
                </div>
                <h3 className="h5 fw-bold text-dark mb-2">No articles found</h3>
                <p className="text-muted mb-4 small" style={{ lineHeight: '1.6' }}>We couldn't find any articles matching "{searchQuery}". Try checking for spelling errors or search for categories like "Work Abroad", "Visa", or "Study Abroad".</p>
                <button id="blog-search-reset-btn" className="premium-btn px-4 py-2 text-white" style={{ borderRadius: '50px' }} onClick={() => setSearchQuery("")}>
                  Reset Search
                </button>
              </motion.div>
            </div>
          ) : (
            filteredBlogs.slice(0, visibleBlogs).map((blog) => (
              <RevealItem
                key={blog.slug}
                direction="up"
                className="col-lg-4 col-md-6"
              >
                <Link to={`/blog/${blog.slug}`} className="blog-card-link text-decoration-none">
                  <div className="blog-card glass-card hover-lift va-hover-lift h-100 p-0 overflow-hidden" style={{ borderRadius: '20px' }}>
                    <div className="position-relative">
                      <img src={blog.image} alt={blog.title} className="w-100" style={{ height: '240px', objectFit: 'cover' }} loading="lazy" />
                      <div className="position-absolute top-0 end-0 m-3">
                        <span className="badge bg-white text-dark shadow-sm px-3 py-2 rounded-pill fw-bold" style={{ fontSize: '0.8rem' }}>{blog.category}</span>
                      </div>
                    </div>
                    <div className="blog-card-body p-4">
                      <span className="blog-date d-block mb-2 text-muted fw-medium" style={{ fontSize: '0.85rem' }}><i className="bi bi-calendar3 me-1"></i> {blog.date}</span>
                      <h2 className="mb-3" style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary-dark)', lineHeight: '1.4' }}>{blog.title}</h2>
                      <p className="text-muted mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{blog.excerpt}</p>
                    </div>
                  </div>
                </Link>
              </RevealItem>
            ))
          )}
        </RevealGroup>

        {visibleBlogs < filteredBlogs.length && (
          <div className="text-center mt-5 pt-3">
            <MagneticButton as="span" className="d-inline-block">
              <button id="blog-load-more-btn" className="premium-btn px-5 py-3 text-white pulse-animation va-shimmer-btn" onClick={handleViewMore}>
                Load More Articles
              </button>
            </MagneticButton>
          </div>
        )}
      </section>
    </>
  );
}
